import * as Sentry from "@sentry/vue";

import { getFlowGuidesByViewName } from "@/api/services/FlowGuide";
import type { FlowGuide } from "@/types/Utils/FlowGuide";

// The welcome tour's anchored steps come from Carbon0 rows, fetched by view name — a single
// source of truth for this content (tutorials, AI Chat, Feedback, etc.) rather than duplicating
// copy per deck.
export type GuideStep = {
  id: string;
  key: string;
  anchorId: string;
  title: string;
  bodyHtml: string;
  alreadySeen: boolean;
  highlight: boolean;
  // Menu key this step holds open for its duration (welcomeFlow.store.ts's heldOpenMenu) — see WelcomeTour.vue.
  holdsOpenMenu?: string;
  // The held-open menu's own panel width (its panel-class min-width) — lets WelcomeTour.vue push the
  // card's popper distance out far enough to clear the menu when it overhangs the anchor button.
  heldMenuWidth?: number;
  // Dropped on an on-demand replay, which starts from the very button the step points at.
  firstRunOnly?: boolean;
};

const escapeHtml = (text: string): string =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// Descriptions are plain text from the DB — split on newlines into real paragraphs instead,
// escaping every line since this is user-editable copy from an API response, never markdown or
// trusted HTML. Rendered through WelcomeTourCard, the welcome tour's own step card.
export const toBodyHtml = (description: string): string =>
  description
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => `<p class="t-body text-type-default">${escapeHtml(line)}</p>`)
    .join("");

// Shared by every welcome-tour deck: each names its own Carbon0 view (the platform deck reads
// "Project Overview", the project deck reads "Project Dashboard"), and both get the same
// sort-by-priority + map-to-GuideStep treatment.
export async function loadFlowGuideSteps(viewName: string): Promise<GuideStep[]> {
  try {
    const flowGuides = await getFlowGuidesByViewName(viewName);
    if (!Array.isArray(flowGuides)) return [];

    return flowGuides
      .slice()
      .sort((a: FlowGuide, b: FlowGuide) => a.priority - b.priority)
      .map((flowGuide: FlowGuide) => ({
        id: flowGuide.id,
        key: flowGuide.id,
        anchorId: flowGuide.elementId,
        title: flowGuide.title,
        bodyHtml: toBodyHtml(flowGuide.description),
        alreadySeen: flowGuide.alreadySeen,
        highlight: true,
      }));
  } catch (error) {
    Sentry.captureException(error);
    return [];
  }
}

export async function loadProjectOverviewGuideSteps(): Promise<GuideStep[]> {
  return loadFlowGuideSteps("Project Overview");
}
